import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowRight, Settings, Shield, TrendingUp, Users, Database, Cloud } from 'lucide-react';

const ITConsulting = () => {
  const capabilities = [
    {
      icon: <Settings className="h-6 w-6" />,
      title: "Digital Strategy",
      description: "Technology roadmaps and digital transformation planning"
    },
    {
      icon: <Cloud className="h-6 w-6" />,
      title: "Cloud Migration",
      description: "Seamless transition to cloud infrastructure and services"
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Security Assessment",
      description: "Comprehensive security audits and implementation strategies"
    },
    {
      icon: <Database className="h-6 w-6" />,
      title: "System Integration",
      description: "Connect disparate systems for improved workflow efficiency"
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Performance Optimization",
      description: "Improve system performance and operational efficiency"
    }
  ];

  const whyVincotek = [
    "Strategic approach focused on long-term business success",
    "Vendor-neutral advice tailored to your specific requirements",
    "Implementation support with hands-on technical expertise", 
    "Ongoing partnership for continuous improvement and growth"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-800 to-vincotek-dark text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              IT Consulting
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Strategic technology consulting to help businesses optimize their digital infrastructure and processes for sustainable growth.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8 text-center">
              What We Do
            </h2>
            <p className="text-lg text-gray-600 mb-12 text-center leading-relaxed">
              Our IT consulting services help businesses make informed technology decisions that drive efficiency and growth. We provide strategic guidance, technical expertise, and implementation support to optimize your technology investments and digital operations.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-6">
                    <div className="text-vincotek-blue mb-4">
                      {capability.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-vincotek-dark mb-3">
                      {capability.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {capability.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8">
              Who This Is For
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Ideal for growing businesses needing technology guidance, companies undergoing digital transformation, and organizations looking to optimize their IT operations. We work with small to medium enterprises, startups scaling their operations, and established businesses seeking to modernize their technology infrastructure and processes.
            </p>
          </div>
        </div>
      </section>

      {/* Why Vincotek Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-12 text-center">
              Why Choose Vincotek
            </h2>
            <div className="space-y-6">
              {whyVincotek.map((reason, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-vincotek-yellow rounded-full flex items-center justify-center">
                    <span className="text-vincotek-dark font-bold text-sm">{index + 1}</span>
                  </div>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {reason}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Optimize Your Technology?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help improve your technology infrastructure and processes.
          </p>
          <Link to="/contact">
            <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
              Contact Us Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ITConsulting;